import React from 'react';
import styled from 'styled-components';
import { FaCheck } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedSection from '../Animation/AnimatedSection';

const DifferentialsSection = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.theme.colors.white};
`;

const DifferentialsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px;
  
  @media screen and (max-width: 768px) {
    text-align: center;
  }
`;

const SectionTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: 3rem;
  text-transform: uppercase;
  font-style: italic;
  
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
  }
`;

const Subheadline = styled(motion.h2)`
  font-size: ${props => props.theme.fontSizes.bodyLg};
  font-weight: ${props => props.theme.fontWeights.medium};
  font-family: ${props => props.theme.fonts.subheading};
  margin: 3rem 0 3rem 0;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  
  @media (max-width: 576px) {
    font-size: 1.1rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const DifferentialsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin: 0 auto;
  width: 100%;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const DifferentialItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  text-align: left;
  padding-right: 8px;
  overflow: hidden;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const IconContainer = styled(motion.div)`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: ${props => props.theme.colors.white};
  
  @media screen and (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 1rem;
  }
`;

const DifferentialContent = styled(motion.div)`
  @media screen and (max-width: 768px) {
    text-align: center;
  }
`;

const DifferentialTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const DifferentialDescription = styled.p`
  margin-bottom: 0.5rem;
`;

const Differentials = () => {
  const differentials = [
    {
      title: "Redução de custos",
      description: "Otimização técnica do produto e prevenção de não conformidades para evitar retrabalhos, repetição de ensaios e gastos desnecessários nos processos de certificação."
    },
    {
      title: "Agilidade nos prazos",
      description: "Execução eficiente e monitoramento contínuo para garantir processos fluindo no tempo certo e sem interrupções inesperadas."
    },
    {
      title: "Atendimento flexível",
      description: "Serviços online ou híbridos, adaptados às necessidades reais de cada cliente e à dinâmica dos projetos."
    },
    {
      title: "Confidencialidade e segurança da informação",
      description: "Proteção rigorosa de dados técnicos e estratégicos, assegurando sigilo total em todas as etapas."
    },
    {
      title: "Parcerias estratégicas",
      description: "Relacionamento direto com laboratórios acreditados e organismos de certificação para assegurar previsibilidade e fluidez nas etapas de testes e avaliações."
    },
    {
      title: "Acompanhamento técnico especializado",
      description: "Suporte contínuo em normas, ensaios, requisitos e adequações, garantindo decisões assertivas e conformidade desde o desenvolvimento até a certificação."
    }
  ];
  
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: { type: "spring", stiffness: 260, damping: 20 }
    },
    hover: { scale: 1.2, transition: { duration: 0.2 } }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.2 + i * 0.1,
        duration: 0.4,
        ease: "easeOut"
      }
    }),
    hover: {
      x: 5,
      transition: { duration: 0.2 }
    }
  };
  
  return (
    <DifferentialsSection id="diferenciais">
      <DifferentialsContainer>
        <SectionTitle
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          Nossos Diferenciais
        </SectionTitle>

        <Subheadline
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Nossos diferenciais são pautados em 6 pilares
        </Subheadline>
        
        <DifferentialsGrid>
          {differentials.map((differential, index) => (
            <AnimatedSection 
              key={index} 
              animation={index % 2 === 0 ? "fadeInLeft" : "fadeInRight"} 
              delay={0.1 * index}
              duration={0.5}
            >
              <DifferentialItem
                custom={index}
                variants={itemVariants}
                whileHover="hover"
              >
                <IconContainer
                  variants={iconVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                >
                  <FaCheck />
                </IconContainer>
                <DifferentialContent>
                  <DifferentialTitle>{differential.title}</DifferentialTitle>
                  <DifferentialDescription>{differential.description}</DifferentialDescription>
                </DifferentialContent>
              </DifferentialItem>
            </AnimatedSection>
          ))}
        </DifferentialsGrid>
      </DifferentialsContainer>
    </DifferentialsSection>
  );
};

export default Differentials;
